<template>
    <div v-bind:class="matchInfoGAClass">
        <div
            class="tournamentLeagueTitle notranslate"
            v-bind:class="[{ _liveTv: liveStatus.tvLive }, { _live: liveStatus.onLive }]"
        >
            <div class="titleGroup">
                <div
                    v-if="isShowLiveBadge"
                    class="liveMarketBadge"
                >
                    {{ liveTranslation }}
                </div>
                <SvgIcon v-if="isComponentInPp" v-bind:iconName="iconName" />
                <div
                    v-if="isShowTodayText"
                    class="match-todaytag"
                >
                    {{ i18n.t('oddsDisplay_matchInfo_header_todayTag') }}
                </div>
                <div class="tournamentLeagueTitleText">
                    {{ leagueName }}
                </div>
            </div>
            <div
                class="refreshBtn"
                v-on:click="refresh(true)"
            >
                <Icons
                    class="iconsSize-18"
                    v-bind:class="{ 'icons-rotated': isRefreshing }"
                    iconName="systemIcon_reload"
                />
            </div>
        </div>
        <div class="tournamentMatchInfo">
            <div class="matchInfoGroup" v-on:click="goToMatchDetail(showVideoFunction)">
                <ul class="teamGroup" v-bind:class="{ _favorite: match.isHomeFavorite }">
                    <li class="teamFlags">
                        <FoulCard
                            v-if="liveStatus.onLive && match.mainMarketEventResult.extraInfo"
                            v-bind:cardNumber="match.mainMarketEventResult.extraInfo.homeRedCards"
                        />
                        <div class="teamSymbol">
                            <picture>
                                <source
                                    v-bind:srcset="`${homeTeamImage.webp}`"
                                    type="image/webp"
                                    class="teamSymbolImg"
                                >
                                <img
                                    v-bind:src="`${homeTeamImage.origin}`"
                                    alt=""
                                    aria-hidden="true"
                                    class="teamSymbolImg"
                                >
                            </picture>
                        </div>
                    </li>
                    <li class="teamName">
                        {{ homeTeamName }}
                    </li>
                </ul>
                <ul class="matchSummary">
                    <template v-if="liveStatus.onLive">
                        <li class="liveGroup">
                            <MatchInfoScore
                                v-if="displayLiveScoreData"
                                v-bind:matchInfo="match"
                                v-bind:isInMatchDetail="false"
                                v-bind:showLiveStreamGifIcon="showLiveStreamGifIcon"
                            />
                        </li>
                    </template>
                    <template v-else>
                        <li class="timeGroup">
                            <div class="date">
                                {{ match.eventDate }}
                            </div>
                            <div class="time">
                                {{ formatShowTime(match.showTime, match.showTimeType) }}
                            </div>
                        </li>
                        <li v-if="isShowCountdown" class="countdownGroup">
                            <span class="num">{{ countdownDisplay.hours }}</span><span>:</span><span class="num">{{ countdownDisplay.minutes }}</span><span>:</span><span class="num">{{ countdownDisplay.seconds }}</span>
                        </li>
                        <li v-else class="countdownGroup">
                            <span class="num">--</span><span>:</span><span class="num">--</span><span>:</span><span class="num">--</span>
                        </li>
                    </template>
                </ul>
                <ul class="teamGroup" v-bind:class="{ _favorite: match.isAwayFavorite }">
                    <li class="teamFlags">
                        <div class="teamSymbol">
                            <picture>
                                <source
                                    v-bind:srcset="`${awayTeamImage.webp}`"
                                    type="image/webp"
                                    class="teamSymbolImg"
                                >
                                <img
                                    v-bind:src="`${awayTeamImage.origin}`"
                                    alt=""
                                    aria-hidden="true"
                                    class="teamSymbolImg"
                                >
                            </picture>
                        </div>
                        <FoulCard
                            v-if="liveStatus.onLive && match.mainMarketEventResult.extraInfo"
                            v-bind:cardNumber="match.mainMarketEventResult.extraInfo.awayRedCards"
                        />
                    </li>
                    <li class="teamName">
                        {{ awayTeamName }}
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
    import { storeToRefs } from 'pinia';
    import type { PropType } from 'vue';
    import { computed, defineComponent, onMounted, onUnmounted, ref, toRefs, watch } from 'vue';
    import { useRoute } from 'vue-router';
    import { useLiveStream } from '../liveStream/useLiveStream';
    import MatchInfoScore from '../match/matchInfo/MatchInfoScore.vue';
    import FoulCard from '@/components/match/FoulCard.vue';
    import useMatchInfoScore from '@/components/match/matchInfo/useMatchInfoScore';
    import { useMatchFunction } from '@/components/matchFunction/useMatchFunction';
    import type { IMatch } from '@/interface/IMatch';
    import { DisplaySection, LiveScoreProvider, MarketPage, MatchFunction, RoutePage, ShowTimeDisplayType } from '@/interface/enum';
    import { useCountdownTimer } from '@/libraries/composables/useCountdownTimer';
    import { addToCalendar } from '@/libraries/helpers/calendarHelper';
    import { getTranslationBySite } from '@/libraries/helpers/i18nHelper';
    import { getSportIconName } from '@/libraries/helpers/iconHelper';
    import { formatShowTime } from '@/libraries/helpers/showTimeHelper';
    import { getTournamentTeamImage } from '@/libraries/helpers/tournamentCenterHelper';
    import { getTranslated } from '@/libraries/helpers/translationHelper';
    import { i18n } from '@/libraries/i18n';
    import { parseDate } from '@/libraries/utils/date';
    import { useProductPlatformStore } from '@/stores/productPlatformStore';
    import Icons from '@/svg/Icons.vue';
    import SvgIcon from '@/svg/SvgIcon.vue';

    export default defineComponent({
        name: 'TournamentMatchInfo',
        components: {
            FoulCard,
            Icons,
            SvgIcon,
            MatchInfoScore,
        },
        props: {
            match: {
                type: Object as PropType<IMatch>,
                required: true,
            },
            marketPageType: {
                type: String as PropType<MarketPage>,
                default: MarketPage.Unknown,
            },
            displaySection: {
                type: Number as PropType<DisplaySection>,
                default: DisplaySection.Unknown,
            },
        },
        setup(props) {
            const { match } = toRefs(props);
            const { isLiveStreamPlayable } = useLiveStream(match);

            const liveStatus = computed(() => ({
                tvLive: match.value.isTvLive,
                sboLive: match.value.isSboLive,
                onLive: match.value.isLive,
                isToday: props.marketPageType === MarketPage.Today,
            }));

            const homeTeamName = computed(() => getTranslated(match.value.homeTeam?.teamName));
            const awayTeamName = computed(() => getTranslated(match.value.awayTeam?.teamName));
            const leagueName = computed(() => getTranslated(match.value.leagueName));

            const liveTranslation = computed(() => getTranslationBySite('live').toUpperCase());

            const isShowLiveBadge = computed(() => liveStatus.value.onLive || liveStatus.value.tvLive || liveStatus.value.sboLive);

            const homeTeamImage = computed(() => getTournamentTeamImage(match.value.homeTeam));
            const awayTeamImage = computed(() => getTournamentTeamImage(match.value.awayTeam));

            const iconName = computed(() => getSportIconName(match.value.sportType));

            const showLiveStreamGifIcon = computed(() => isLiveStreamPlayable.value);

            const route = useRoute();
            const isMyMatchPage = computed(() => route.name === RoutePage.MyMatches);
            const { isComponentInPp } = storeToRefs(useProductPlatformStore());

            const isShowTodayText = computed(() => !liveStatus.value.onLive && liveStatus.value.isToday && (isMyMatchPage.value || isComponentInPp.value));

            const ticks = ref(0);
            const isRefreshing = ref(false);

            const refreshDuration = computed(() => (match.value.isLive ? 30 : 60));

            const isShowCountdown = computed(() => {
                if (match.value.isLive) return false;
                if (!match.value.showTimeType) return false;
                if (match.value.showTimeType & ShowTimeDisplayType.Date) return false;
                return true;
            });

            const { goToMatchDetail } = useMatchFunction(match);

            const showVideoFunction = computed(() => ({ matchFunctionTab: showLiveStreamGifIcon.value ? MatchFunction.LiveStream : MatchFunction.Unknown }));

            const {
                displayLiveScoreData,
                liveScoreInfo,
            } = useMatchInfoScore(match);

            const cricketRunningTeamClass = ref('running-team-none');

            watch(liveScoreInfo, (newValue, oldValue) => {
                if (displayLiveScoreData.value?.scoreProvider !== LiveScoreProvider.Cricket || !newValue?.cricket || !oldValue?.cricket) {
                    cricketRunningTeamClass.value = 'running-team-none';
                    return;
                }
                const newCricket = newValue.cricket;
                const oldCricket = oldValue.cricket;
                if (newCricket.homeScore > oldCricket.homeScore || newCricket.homeDismissals > oldCricket.homeDismissals) {
                    cricketRunningTeamClass.value = 'running-team-home';
                } else if (newCricket.awayScore > oldCricket.awayScore || newCricket.awayDismissals > oldCricket.awayDismissals) {
                    cricketRunningTeamClass.value = 'running-team-away';
                } else {
                    cricketRunningTeamClass.value = 'running-team-none';
                }
            });

            const localShowTime = computed(() => parseDate(match.value.showTime, 'MM/dd/yyyy HH:mm', 8));
            const { formattedTime: countdownDisplay } = useCountdownTimer(localShowTime);

            const matchInfoGAClass = computed(() => {
                if (props.displaySection === DisplaySection.TournamentCenter_UpcomingMatches) {
                    return 'TC_Upcoming_MatchCard';
                }
                if (props.displaySection === DisplaySection.TournamentCenter_Country) {
                    return 'TC_Nations_MatchCard';
                }
                return '';
            });

            function tick() {
                ticks.value++;
                if (ticks.value >= refreshDuration.value) {
                    refresh();
                }
            }

            function refresh(force: boolean = false) {
                if (isRefreshing.value) return;

                ticks.value = 0;

                if (force) {
                    realRefresh();
                } else {
                    fakeRefresh();
                }
            }

            function fakeRefresh() {
                refreshAnimation();
            }

            function realRefresh() {
                refreshAnimation();

                window.eventEmitter.emit('refreshMatch', match.value.eventId, true);
            }

            function refreshAnimation() {
                isRefreshing.value = true;
                setTimeout(() => {
                    isRefreshing.value = false;
                }, 1000);
            }

            onMounted(() => {
                window.eventEmitter.on('tick', tick);
            });

            onUnmounted(() => {
                window.eventEmitter.off('tick', tick);
            });

            return {
                liveStatus,
                i18n,
                homeTeamName,
                awayTeamName,
                homeTeamImage,
                awayTeamImage,
                leagueName,
                showLiveStreamGifIcon,
                isMyMatchPage,
                isComponentInPp,
                getTranslationBySite,
                formatShowTime,
                addToCalendar,
                refresh,
                displayLiveScoreData,
                isShowCountdown,
                countdownDisplay,
                liveTranslation,
                isShowLiveBadge,
                isShowTodayText,
                iconName,
                goToMatchDetail,
                showVideoFunction,
                isRefreshing,
                matchInfoGAClass,
                cricketRunningTeamClass,
            };
        },
    });
</script>

<style lang="scss" scoped>
@import "@/scss/components/tournamentMatchInfo";
</style>
